import {
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
  DateRangePreset,
  getDateRangeIssue,
  getPresetRange,
} from '@employ-engage/data';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Picking a span of days: presets, or two dates.
 *
 * Lifted out of the export modal, which was the only place in the app with a
 * working one. The reporting pages offered four fixed ranges and no way to ask
 * for an arbitrary window at all, even though the server has supported
 * `{mode:'absolute'}` from the start.
 *
 * Dates are bare `yyyy-MM-dd` throughout — no time, no zone. A chart spec is
 * stored and re-run later, possibly by someone in another timezone, and an
 * instant anchored to whoever first picked it would silently shift the window.
 * The server widens a bare date to the whole UTC day at both ends.
 */

/**
 * A date input emits on every keystroke, so typing a year produces
 * "0002-01-01", "0020-01-01", "0202-01-01" before "2026-01-01". Without a
 * pause each of those partial years fires its own query.
 */
export const DATE_INPUT_DEBOUNCE_MS = 500;

const INPUT_CLASSES =
  'mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-dark-primaryBackground dark:text-gray-200';

export interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  /** Called only once the pair is worth acting on — see the debounce note. */
  onChange: (range: { startDate: string; endDate: string }) => void;
  /** Omit the presets when the caller offers its own. */
  showPresets?: boolean;
  legend?: string;
  className?: string;
}

/** Which preset, if any, the current pair happens to match. */
export const presetMatching = (
  startDate: string,
  endDate: string,
  today: Date = new Date()
) =>
  DATE_RANGE_PRESETS.find((preset) => {
    const range = getPresetRange(preset, today);
    return range.startDate === startDate && range.endDate === endDate;
  });

export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  showPresets = true,
  legend,
  className,
}: DateRangePickerProps) {
  const { t } = useTranslation();

  // Typing is local; the caller hears about it once the value settles.
  const [draft, setDraft] = useState({ startDate, endDate });
  const [touched, setTouched] = useState({ startDate: false, endDate: false });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A preset button, or a new range from outside, replaces what is being typed.
  useEffect(() => {
    setDraft({ startDate, endDate });
  }, [startDate, endDate]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const emitLater = (next: { startDate: string; endDate: string }) => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // A half-typed or backwards range is not worth querying for.
      if (!getDateRangeIssue(next.startDate, next.endDate)) onChange(next);
    }, DATE_INPUT_DEBOUNCE_MS);
  };

  /**
   * Take whatever the field actually holds once it is left.
   *
   * Edge's calendar can fill a date field without React seeing a change, so
   * the field shows a date while the draft still holds the empty value from
   * before. The draft then reads as invalid, the warning appears beside a date
   * that looks fine, and the range never reaches the caller.
   */
  const leave = (field: 'startDate' | 'endDate', value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (value !== draft[field]) emitLater({ ...draft, [field]: value });
  };

  const applyPreset = (preset: DateRangePreset) => {
    if (timer.current) clearTimeout(timer.current);
    const range = getPresetRange(preset, new Date());
    setDraft(range);
    onChange(range);
  };

  const issue = getDateRangeIssue(draft.startDate, draft.endDate);
  // A half-typed date is expected mid-edit and waits for the field to be left.
  // A backwards range is a real mistake and says so at once.
  const message =
    issue === 'start-after-end'
      ? t('Start date must be on or before the end date.')
      : issue === 'start-invalid' && touched.startDate
      ? t("Start date isn't a valid date.")
      : issue === 'end-invalid' && touched.endDate
      ? t("End date isn't a valid date.")
      : null;

  const active = presetMatching(draft.startDate, draft.endDate);

  return (
    <fieldset className={className}>
      {legend && (
        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {legend}
        </legend>
      )}

      {showPresets && (
        <div className="mt-2 flex flex-wrap gap-2">
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              aria-pressed={active === preset}
              onClick={() => applyPreset(preset)}
              className={classNames(
                'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                active === preset
                  ? 'border-light-primary bg-light-primary text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:bg-dark-primaryBackground dark:text-gray-200'
              )}
            >
              {t(DATE_RANGE_PRESET_LABELS[preset])}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-300">
            {t('From')}
            <input
              className={INPUT_CLASSES}
              type="date"
              value={draft.startDate}
              onBlur={(e) => leave('startDate', e.currentTarget.value)}
              onChange={(e) =>
                emitLater({ ...draft, startDate: e.target.value })
              }
            />
          </label>
        </div>
        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-300">
            {t('To')}
            <input
              className={INPUT_CLASSES}
              type="date"
              value={draft.endDate}
              onBlur={(e) => leave('endDate', e.currentTarget.value)}
              onChange={(e) => emitLater({ ...draft, endDate: e.target.value })}
            />
          </label>
        </div>
      </div>

      {message && (
        <p className="mt-2 text-sm text-red-500" role="alert">
          {message}
        </p>
      )}
    </fieldset>
  );
}

export default DateRangePicker;
